import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { latLngBounds, LeafletMouseEvent, Rectangle, rectangle } from 'leaflet';
import { MapMode, MapModes } from './shared';

@Injectable()
abstract class BaseDrawZoomMode<
    T extends { mode: 'zoomin' } | { mode: 'zoomout' },
  >
  extends MapMode<T>
  implements OnDestroy
{
  private readonly zone = inject(NgZone);

  setup() {
    this.map.dragging.disable();
    this.map.on('mousedown', this.handleMouseDown);
  }

  private readonly handleMouseDown = (event: LeafletMouseEvent) => {
    this.zone.run(() => {
      const map = this.map;
      const mapEl = map.getContainer();

      if (mapEl !== event.originalEvent.target) {
        return;
      }

      const start = event.latlng;
      let rect: Rectangle | null = null;

      const handleMouseMove = (event: LeafletMouseEvent) => {
        const end = event.latlng;
        const bounds = latLngBounds(start, end);
        if (rect) {
          rect.setBounds(bounds);
        } else {
          rect = rectangle(bounds).addTo(map);
        }
      };

      const handleMouseUp = (event: LeafletMouseEvent) => {
        const end = event.latlng;
        const bounds = latLngBounds(start, end);

        if (this.options.mode === 'zoomout') {
          map.setView(bounds.getCenter(), map.getZoom() - 1);
        } else if (this.options.mode === 'zoomin') {
          map.fitBounds(bounds);
        }

        rect?.remove();
        rect = null;
        map.off('mousemove', handleMouseMove);
      };

      map.on('mousemove', handleMouseMove);
      map.once('mouseup', handleMouseUp);
    });
  };

  ngOnDestroy() {
    this.map.dragging.enable();
    this.map.off('mousedown', this.handleMouseDown);
  }
}

@Injectable()
export class DrawZoomInMode extends BaseDrawZoomMode<{ mode: 'zoomin' }> {
  override type = MapModes.DRAW_ZOOM_IN;
}

@Injectable()
export class DrawZoomOutMode extends BaseDrawZoomMode<{ mode: 'zoomout' }> {
  override type = MapModes.DRAW_ZOOM_OUT;
}
